import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Youtube, Play, Clock, Eye, TrendingUp, Newspaper, DollarSign, Users, Briefcase, Home, UtensilsCrossed, Star, MapPin, Bookmark } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Video, Restaurant, videoCategories, regions } from "@shared/schema";
import { toArray } from "@/lib/array";
import { normalizeImageUrl } from "@/lib/image-url";
import { useState } from "react";
import { Link } from "wouter";
import Seo from "@/components/seo/Seo";
import { PageContainer } from "@/components/layout/PageContainer";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// 영상 카드의 찜하기 버튼 - featured/regular 두 카드 블록에서 공유.
// itemType: "video"는 functions/api/bookmarks/index.ts의 VALID_ITEM_TYPES에 포함되어 있고
// MyPage.tsx의 "동영상" 탭이 이 버튼으로 생성된 북마크를 표시한다.
function VideoBookmarkButton({ video }: { video: Video }) {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  const { data: bookmarkData } = useQuery<{ isBookmarked: boolean; bookmarkId?: string }>({
    queryKey: ["/api/bookmarks/check", user?.uid, "video", video.id],
    queryFn: async () => {
      const params = new URLSearchParams({
        userId: user?.uid || "",
        itemType: "video",
        itemId: String(video.id ?? ""),
      });
      const res = await fetch(`/api/bookmarks/check?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to check bookmark");
      return res.json();
    },
    enabled: isAuthenticated && !!user,
  });
  const isBookmarked = bookmarkData?.isBookmarked || false;

  const addBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      return await apiRequest("POST", "/api/bookmarks", {
        userId: user.uid,
        itemType: "video",
        itemId: video.id,
        itemTitle: video.titleKo || video.title,
        itemImageUrl: normalizeImageUrl(video.thumbnailUrl) || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "video", video.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({ title: "찜하기 완료", description: "마이페이지에서 확인하실 수 있습니다" });
    },
    onError: (error: any) => {
      toast({ title: "찜하기 실패", description: error.message, variant: "destructive" });
    },
  });

  const removeBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!bookmarkData?.bookmarkId) throw new Error("북마크 ID를 찾을 수 없습니다");
      return await apiRequest("DELETE", `/api/bookmarks/${bookmarkData.bookmarkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "video", video.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({ title: "찜하기 취소", description: "찜하기가 해제되었습니다" });
    },
    onError: (error: any) => {
      toast({ title: "찜하기 취소 실패", description: error.message, variant: "destructive" });
    },
  });

  if (!isAuthenticated) return null;

  const isPending = addBookmarkMutation.isPending || removeBookmarkMutation.isPending;

  return (
    <Button
      variant="ghost"
      size="icon"
      className={`h-7 w-7 shrink-0 ${isBookmarked ? "text-primary" : "text-muted-foreground"}`}
      disabled={isPending}
      data-testid={`button-bookmark-video-${video.id}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isBookmarked) {
          removeBookmarkMutation.mutate();
        } else {
          addBookmarkMutation.mutate();
        }
      }}
    >
      <Bookmark className={`h-4 w-4 ${isBookmarked ? "fill-current" : ""}`} />
    </Button>
  );
}

export default function YouTubePage() {
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [regionFilter, setRegionFilter] = useState<string>("ALL");

  const { data: rawVideos, isLoading } = useQuery({
    queryKey: ["/api/videos", { status: "published" }],
  });
  const videos = toArray<Video>(rawVideos);

  const { data: rawRestaurants } = useQuery({
    queryKey: ['/api/restaurants'],
  });
  const restaurants = toArray<Restaurant>(rawRestaurants);

  const featuredRestaurants = restaurants.filter(r => r.status === 'active' && r.featured).slice(0, 4);

  const filteredVideos = videos.filter(video => {
    if (categoryFilter !== "all" && video.category !== categoryFilter) return false;
    if (regionFilter !== "ALL" && video.region !== regionFilter) return false;
    return true;
  });

  const featuredVideos = filteredVideos.filter(v => v.isFeatured).slice(0, 1);
  const regularVideos = filteredVideos.filter(v => !v.isFeatured);

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, any> = {
      "stock-korea": TrendingUp,
      "stock-usa": TrendingUp,
      "stock-australia": TrendingUp,
      "realestate": Home,
      "news-summary": Newspaper,
      "interview": Users,
      "business-review": Briefcase,
    };
    return icons[category] || Youtube;
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatViews = (views: number | null) => {
    if (!views) return "0";
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };

  return (
    <>
      <Seo
        title="YouTube — OZKNEWS"
        description="뉴스 요약, 주식·부동산 시황, 교민 인터뷰 등 다양한 영상 콘텐츠. OZK News 유튜브 채널에서 만나보세요."
        path="/youtube"
        keywords={["유튜브", "영상 뉴스", "주식 시황", "부동산", "교민 인터뷰", "뉴스 요약"]}
      />

      <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-b">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-3 mb-4">
            <Youtube className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">OZK News YouTube</h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mb-6">
            뉴스 요약, 주식·부동산 시황, 교민 인터뷰 등 다양한 영상 콘텐츠
          </p>
          <Button size="lg" className="gap-2" data-testid="button-subscribe">
            <Youtube className="h-5 w-5" />
            채널 구독하기
          </Button>
        </div>
      </div>

      <PageContainer size="wide">
        {/* Filter Section */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="flex-1 min-w-[200px]">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="select-category-filter">
                <SelectValue placeholder="카테고리 선택" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체 카테고리</SelectItem>
                {videoCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger data-testid="select-region-filter">
                <SelectValue placeholder="지역 선택" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((region) => (
                  <SelectItem key={region.id} value={region.id}>
                    {region.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Categories */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-12">
          {videoCategories.map((category) => {
            const Icon = getCategoryIcon(category.id);
            const count = filteredVideos.filter(v => v.category === category.id).length;

            return (
              <Card
                key={category.id}
                className="hover-elevate active-elevate-2 cursor-pointer"
                data-testid={`card-category-${category.id}`}
                onClick={() => setCategoryFilter(category.id)}
              >
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg">{category.label}</CardTitle>
                      <p className="text-sm text-muted-foreground">{count}개 영상</p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">로딩 중...</p>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="text-center py-12">
            <Youtube className="h-24 w-24 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">영상이 없습니다</h3>
            <p className="text-muted-foreground mb-6">
              곧 다양한 영상을 만나보실 수 있습니다
            </p>
          </div>
        ) : (
          <>
            {/* Featured Video Section */}
            {featuredVideos.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold mb-6">추천 영상</h2>
                {featuredVideos.map((video) => {
                  const categoryInfo = videoCategories.find(c => c.id === video.category);
                  return (
                    <Link key={video.id} href={`/youtube/${video.id}`}>
                      <Card className="overflow-hidden hover-elevate active-elevate-2 cursor-pointer" data-testid={`card-featured-${video.id}`}>
                        <div className="aspect-video bg-muted relative group">
                          {video.thumbnailUrl ? (
                            <img
                              src={normalizeImageUrl(video.thumbnailUrl) || ''}
                              alt={video.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <Youtube className="h-24 w-24 text-muted-foreground" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="p-4 rounded-full bg-primary">
                              <Play className="h-8 w-8 text-primary-foreground" />
                            </div>
                          </div>
                          {video.duration && (
                            <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-sm">
                              {formatDuration(video.duration)}
                            </div>
                          )}
                        </div>
                        <CardHeader>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-xs">{categoryInfo?.label}</Badge>
                              {video.region !== "ALL" && (
                                <Badge variant="outline" className="text-xs">{video.region}</Badge>
                              )}
                            </div>
                            <VideoBookmarkButton video={video} />
                          </div>
                          <CardTitle className="text-xl">{video.titleKo || video.title}</CardTitle>
                          {(video.descriptionKo || video.description) && (
                            <CardDescription className="line-clamp-2">
                              {video.descriptionKo || video.description}
                            </CardDescription>
                          )}
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Eye className="h-4 w-4" />
                              <span>{formatViews(video.views)} 조회</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              <span>{new Date(video.publishedAt).toLocaleDateString('ko-KR')}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Regular Videos Grid */}
            {regularVideos.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-6">전체 영상</h2>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {regularVideos.map((video) => {
                    const categoryInfo = videoCategories.find(c => c.id === video.category);
                    return (
                      <Link key={video.id} href={`/youtube/${video.id}`}>
                        <Card className="hover-elevate active-elevate-2 cursor-pointer h-full flex flex-col" data-testid={`card-video-${video.id}`}>
                          <div className="aspect-video bg-muted relative group rounded-t-lg overflow-hidden">
                            {video.thumbnailUrl ? (
                              <img
                                src={normalizeImageUrl(video.thumbnailUrl) || ''}
                                alt={video.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Youtube className="h-16 w-16 text-muted-foreground" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <div className="p-3 rounded-full bg-primary">
                                <Play className="h-6 w-6 text-primary-foreground" />
                              </div>
                            </div>
                            {video.duration && (
                              <div className="absolute bottom-2 right-2 bg-black/80 text-white px-2 py-1 rounded text-sm">
                                {formatDuration(video.duration)}
                              </div>
                            )}
                          </div>
                          <CardHeader className="flex-1">
                            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="text-xs">{categoryInfo?.label}</Badge>
                                {video.region !== "ALL" && (
                                  <Badge variant="outline" className="text-xs">{video.region}</Badge>
                                )}
                              </div>
                              <VideoBookmarkButton video={video} />
                            </div>
                            <CardTitle className="text-base line-clamp-2">{video.titleKo || video.title}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                <span>{formatViews(video.views)}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>{formatDuration(video.duration)}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Featured Restaurants Section */}
        {featuredRestaurants.length > 0 && (
          <div className="mt-16 mb-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <UtensilsCrossed className="h-7 w-7 text-primary" />
                <h2 className="text-2xl font-bold">추천 맛집</h2>
              </div>
              <Link href="/marketplace?tab=restaurants">
                <Button variant="outline" className="gap-2" data-testid="link-view-all-restaurants">
                  모든 맛집 보기
                  <UtensilsCrossed className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {featuredRestaurants.map((restaurant) => (
                <Link key={restaurant.id} href={`/marketplace?tab=restaurants&id=${restaurant.id}`}>
                  <Card className="hover-elevate active-elevate-2 cursor-pointer h-full" data-testid={`card-restaurant-${restaurant.id}`}>
                    <div className="aspect-video bg-muted relative rounded-t-lg overflow-hidden">
                      {(restaurant.coverImageUrl || restaurant.logoUrl) ? (
                        <img
                          src={normalizeImageUrl(restaurant.coverImageUrl || restaurant.logoUrl) || ''}
                          alt={restaurant.restaurantNameKo || restaurant.restaurantName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <UtensilsCrossed className="h-16 w-16 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant="secondary" className="text-xs">
                          {restaurant.category === 'korean' && '한식'}
                          {restaurant.category === 'chinese' && '중식'}
                          {restaurant.category === 'japanese' && '일식'}
                          {restaurant.category === 'western' && '양식'}
                          {restaurant.category === 'vietnamese' && '베트남'}
                          {restaurant.category === 'thai' && '태국'}
                          {restaurant.category === 'cafe' && '카페'}
                          {restaurant.category === 'other' && '기타'}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {restaurant.priceRange === 'budget' && '$'}
                          {restaurant.priceRange === 'moderate' && '$$'}
                          {restaurant.priceRange === 'high' && '$$$'}
                        </Badge>
                      </div>
                      <CardTitle className="text-base line-clamp-1">
                        {restaurant.restaurantNameKo || restaurant.restaurantName}
                      </CardTitle>
                      {restaurant.shortDescription && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {restaurant.shortDescription}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {restaurant.averageRating && parseFloat(restaurant.averageRating) > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <span>{parseFloat(restaurant.averageRating).toFixed(1)}</span>
                          </div>
                        )}
                        {restaurant.address && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            <span className="line-clamp-1">{restaurant.city || restaurant.state}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <Card className="mt-12 bg-primary/5 border-primary/20">
          <CardContent className="py-12 text-center">
            <Youtube className="h-16 w-16 mx-auto text-primary mb-4" />
            <h3 className="text-2xl font-bold mb-2">채널 구독하고 최신 영상을 받아보세요</h3>
            <p className="text-muted-foreground mb-6">
              매주 새로운 영상이 업데이트됩니다
            </p>
            <Button size="lg" className="gap-2" data-testid="button-subscribe-bottom">
              <Youtube className="h-5 w-5" />
              지금 구독하기
            </Button>
          </CardContent>
        </Card>
        </PageContainer>
      </div>
    </>
  );
}
